import { DataUtils as utils } from './data-utils.js';
import { unwrapProxy } from '../../data-proxy.js';

/**
 * Module-level Set reused across publish calls to detect circular references.
 * Using a persistent Set (cleared before each top-level call) avoids
 * allocating a new WeakSet on every data mutation.
 *
 * `Set` is used instead of `WeakSet` because Set has `clear()`,
 * allowing the same instance to be reused without GC pressure.
 * Strong references are held only for the duration of one publish cycle,
 * then released via `clear()`.
 *
 * @type {Set<object>}
 */
const _seen = new Set();

/**
 * Publisher
 *
 * This will contain methods for publishing data.
 *
 * @class
 */
export class Publisher
{
	/**
	 * Depth limit for nested object publishing to prevent infinite recursion
	 * @type {number}
	 */
	static MAX_DEPTH = 50;

	/**
	 * This will publish deep data.
	 *
     * @param {object} obj
	 * @param {string} attr
	 * @param {*} val
     * @param {function} callBack
	 * @returns {void}
	 */
	static publishDeep(obj, attr, val, callBack)
	{
		if (!utils.hasDeepData(attr))
		{
			this.publish(attr, val, callBack);
			return;
		}

		let prop,
		props = utils.getSegments(attr);
		if (!props)
		{
			return;
		}

		const length = props.length,
		end = length - 1;

		/* the path is a string equivalent of the javascript dot notation path
		of the object being published. */
		let path = '';
		for (let i = 0; i < length; i++)
		{
			const prop = props[i];

			/* we need to setup the object to go to the next level
			of the data object before calling the next property. */
			obj = obj[prop];

			/* Digit check (charCode 48='0' to 57='9') replaces isNaN(prop). */
			const c = prop.charCodeAt(0);

			if (i > 0)
			{
				if (c < 48 || c > 57)
				{
					path += '.' + prop;
				}
			}
			else
			{
				path = prop;
			}

			let publish;
			if (i === end)
			{
				publish = val;
			}
			else
			{
				const nextProp = props[i + 1];
				const nc = nextProp.charCodeAt(0);
				if (nc >= 48 && nc <= 57)
				{
					path += '[' + nextProp + ']';
					continue;
				}

				const nextAttr = {};
				nextAttr[nextProp] = obj[nextProp];
				publish = nextAttr;
			}

			this.publish(path, publish, callBack);
		}
	}

	/**
	 * This will publish an update to the data binder.
	 *
	 * @param {string} pathString
	 * @param {*} obj
     * @param {function} callBack
	 * @param {?WeakSet} [seen] - Set of seen objects to detect circular refs
	 * @param {number} [depth] - Current recursion depth
	 * @returns {void}
	 */
	static publish(pathString, obj, callBack, seen = null, depth = 0)
	{
		/**
		 * This will publish the data path to get the
		 * root attr before publishing deeper.
		 */
		pathString = pathString || "";
		callBack(pathString, obj);

		if (!obj || typeof obj !== 'object')
		{
			return;
		}

		/**
		 * Unwrap any DataProxy to its raw target before walking.
		 * Iterating a proxy here would invoke the get trap on
		 * every nested key and re-wrap each child in another
		 * proxy, which dominated the resume/republish flame for
		 * components with large nested data.
		 */
		obj = unwrapProxy(obj);

		// On top-level call, reuse module-level Set to avoid heap allocation
		const isTopLevel = (seen === null);
		if (isTopLevel)
		{
			/* Clear at the START of each top-level call (not the end) so the
			 * same Set instance is safely reused without GC pressure. */
			_seen.clear();
			seen = _seen;
		}

		// seen is guaranteed non-null at this point; guard satisfies TS flow analysis
		if (!seen) { return; }

		// Detect circular reference
		if (seen.has(obj))
		{
			console.warn('[Publisher] Circular reference detected at path:', pathString);
			return;
		}

		// Depth limit to prevent infinite recursion
		if (depth >= this.MAX_DEPTH)
		{
			console.warn('[Publisher] Max depth exceeded at path:', pathString, '- stopping recursion');
			return;
		}

		// Mark this object as seen
		seen.add(obj);

		if (Array.isArray(obj))
		{
			this.publishArray(pathString, obj, callBack, seen, depth);
		}
		else
		{
			this.publishObject(pathString, obj, callBack, seen, depth);
		}
	}

    /**
     * This will publish an array to the data binder.
     *
     * @protected
     * @param {string} pathString
     * @param {Array<any>} obj
     * @param {function} callBack
     * @param {WeakSet} seen - Set of seen objects
     * @param {number} depth - Current recursion depth
     * @returns {void}
     */
    static publishArray(pathString, obj, callBack, seen, depth)
    {
        const length = obj.length;
        for (let i = 0; i < length; i++)
        {
            const value = obj[i];
            const subPath = pathString + '[' + i + ']';
            this._checkPublish(subPath, value, callBack, seen, depth);
        }
    }

    /**
     * This will publish an object to the data binder.
     *
     * @protected
     * @param {string} pathString
     * @param {object} obj
     * @param {function} callBack
     * @param {WeakSet} seen - Set of seen objects
     * @param {number} depth - Current recursion depth
     * @returns {void}
     */
    static publishObject(pathString, obj, callBack, seen, depth)
    {
        /* Object.keys() + indexed loop is faster than for..in + hasOwnProperty.call
         * for plain objects: no prototype chain traversal and single allocation
         * that the engine can optimise away in the common path. */
        const keys = Object.keys(obj);
        for (let i = 0, len = keys.length; i < len; i++)
        {
            const prop = keys[i];
            const value = obj[prop];
            /* String + concat is at least as fast as a template literal and avoids
             * the tagged-template overhead on older JIT builds. */
            const subPath = pathString + '.' + prop;
            this._checkPublish(subPath, value, callBack, seen, depth);
        }
    }

	/**
	 * This will check if the value is an object and publish
	 * the value or the object.
	 *
	 * @protected
	 * @param {string} subPath
	 * @param {*} val
     * @param {function} callBack
     * @param {WeakSet} seen - Set of seen objects
     * @param {number} depth - Current recursion depth
	 * @returns {void}
	 */
	static _checkPublish(subPath, val, callBack, seen, depth)
	{
		if (!val || typeof val !== 'object')
		{
			callBack(subPath, val);
            return;
		}

        this.publish(subPath, val, callBack, seen, depth + 1);
	}
}